// Représentations (H7-02) : agir au nom d'une entreprise, d'une association ou d'une personne
// dans une collectivité, après dépôt d'un mandat et d'un extrait de registre validés par un agent.
// Liste avec suivi de la validation, formulaire de demande (multipart : mandat et extrait).
import { Bandeau, Bouton, Champ, EtatVide, FichierChamp, Formulaire, Selection } from '@cc/ui';
import { Handshake } from 'lucide-react';
import { useState } from 'react';
import { requete } from '../../../api/client';
import { EtatRequete } from '../../../commun/EtatRequete';
import { date } from '../../../commun/format';
import { useAction } from '../../../commun/useAction';
import { CLES, useRepresentations } from '../api';
import { useCollectiviteChoisie, useCollectivitesPubliques } from '../collectivite';
import { EnTeteCitoyen, ExigeConnexion, Ligne, Liste, Section, TexteStatut } from '../commun';
import { LIBELLES_STATUT_REPRESENTATION, LIBELLES_TYPE_ENTITE } from '../representation';

const ACCEPTE = '.pdf,.jpg,.jpeg,.png';

function FormulaireRepresentation({ onFini }: { onFini: () => void }) {
  const [choisie] = useCollectiviteChoisie();
  const collectivites = useCollectivitesPubliques();
  const [collectivite, setCollectivite] = useState(choisie?.code ?? '');
  const [nom, setNom] = useState('');
  const [type, setType] = useState<'entreprise' | 'association' | 'personne'>('entreprise');
  const [registre, setRegistre] = useState('');
  const [mandat, setMandat] = useState<File[]>([]);
  const [extrait, setExtrait] = useState<File[]>([]);
  const [locales, setLocales] = useState<{ champ: string; message: string }[]>([]);
  const demande = useAction({
    executer: () => {
      const corps = new FormData();
      corps.set('collectivite', collectivite);
      corps.set('entite_nom', nom.trim());
      corps.set('entite_type', type);
      if (registre.trim()) corps.set('entite_registre', registre.trim());
      if (mandat[0]) corps.set('mandat', mandat[0]);
      if (extrait[0]) corps.set('extrait', extrait[0]);
      return requete('/usager/representations', { methode: 'POST', corps });
    },
    succes: 'Demande de représentation déposée : un agent de la collectivité la validera.',
    invalider: [[...CLES.representations]],
    apres: onFini,
  });
  const erreurs = [...locales, ...demande.erreursChamps.map((e) => ({ champ: `rep-${e.champ.replace(/_/g, '-')}`, message: e.message }))];
  const erreurChamp = (champ: string) => erreurs.find((e) => e.champ === champ)?.message ?? null;
  return (
    <Formulaire
      libelle="Demande de représentation"
      onSoumettre={() => {
        const l = [
          !collectivite ? { champ: 'rep-collectivite', message: 'Choisissez la collectivité' } : null,
          nom.trim().length < 2 ? { champ: 'rep-entite-nom', message: 'Le nom de l\'entité est obligatoire' } : null,
          !mandat[0] ? { champ: 'rep-mandat', message: 'Le mandat est obligatoire' } : null,
          !extrait[0] ? { champ: 'rep-extrait', message: 'L\'extrait de registre est obligatoire' } : null,
        ].filter((x): x is { champ: string; message: string } => x !== null);
        setLocales(l);
        if (l.length === 0) demande.lancer(undefined);
      }}
      erreurs={erreurs}
      actions={
        <Bouton type="submit" chargement={demande.enCours} libelleChargement="Envoi en cours">
          Demander la représentation
        </Bouton>
      }
    >
      {demande.messageErreur && demande.erreursChamps.length === 0 ? <Bandeau type="erreur">{demande.messageErreur}</Bandeau> : null}
      <Selection
        id="rep-collectivite"
        libelle="Collectivité où la représentation sera utilisée"
        optionVide="Choisir"
        options={(collectivites.data ?? []).map((c) => ({ valeur: c.code, libelle: c.nom }))}
        value={collectivite}
        onChange={(e) => setCollectivite(e.target.value)}
        erreur={erreurChamp('rep-collectivite')}
        required
      />
      <Champ id="rep-entite-nom" libelle="Nom de l'entité représentée" value={nom} onChange={(e) => setNom(e.target.value)} erreur={erreurChamp('rep-entite-nom')} required />
      <Selection
        id="rep-entite-type"
        libelle="Type d'entité"
        options={Object.entries(LIBELLES_TYPE_ENTITE).map(([valeur, libelle]) => ({ valeur, libelle }))}
        value={type}
        onChange={(e) => setType(e.target.value as typeof type)}
      />
      <Champ id="rep-entite-registre" libelle="Numéro de registre (RCCM, récépissé)" value={registre} onChange={(e) => setRegistre(e.target.value)} facultatif aide="Permet à un autre mandataire de la même entité de voir les mêmes dossiers" />
      <FichierChamp id="rep-mandat" libelle="Mandat de représentation" fichiers={mandat} onFichiers={setMandat} multiple={false} accept={ACCEPTE} erreur={erreurChamp('rep-mandat')} required />
      <FichierChamp id="rep-extrait" libelle="Extrait de registre" fichiers={extrait} onFichiers={setExtrait} multiple={false} accept={ACCEPTE} erreur={erreurChamp('rep-extrait')} required />
    </Formulaire>
  );
}

export function PageRepresentations() {
  const representations = useRepresentations();
  const [ajout, setAjout] = useState(false);
  return (
    <>
      <EnTeteCitoyen
        titre="Représentations"
        sousTitre="Déposez des demandes au nom d'une entreprise ou d'une association. La collectivité valide chaque représentation pour une durée."
        retour="/citoyen/compte"
        libelleRetour="Mon compte"
        actions={
          !ajout ? (
            <Bouton variante="principal" onClick={() => setAjout(true)}>
              Ajouter une entité
            </Bouton>
          ) : null
        }
      />
      <ExigeConnexion>
        {ajout ? (
          <Section titre="Nouvelle représentation">
            <FormulaireRepresentation onFini={() => setAjout(false)} />
            <div>
              <Bouton variante="discret" onClick={() => setAjout(false)}>
                Annuler
              </Bouton>
            </div>
          </Section>
        ) : null}
        <Section titre="Mes représentations">
          <EtatRequete chargement={representations.isPending} erreur={representations.error} onReessayer={() => void representations.refetch()} fonction="Les représentations">
            {(representations.data ?? []).length === 0 ? (
              <EtatVide titre="Aucune représentation" icone={Handshake}>
                <p>Vous agissez en votre nom propre. Ajoutez une entité pour déposer des demandes en son nom.</p>
              </EtatVide>
            ) : (
              <Liste libelle="Représentations">
                {(representations.data ?? []).map((r) => (
                  <Ligne
                    key={r.id}
                    titre={r.entite_nom}
                    sousTitre={`${LIBELLES_TYPE_ENTITE[r.entite_type] ?? r.entite_type} · ${r.collectivite.nom}${r.fin && r.statut === 'validee' ? ` · jusqu'au ${date(r.fin)}` : ''}${r.motif_refus ? ` · ${r.motif_refus}` : ''}`}
                    droite={
                      <TexteStatut
                        libelle={r.statut === 'validee' && !r.valide ? 'Expirée' : LIBELLES_STATUT_REPRESENTATION[r.statut] ?? r.statut}
                        ton={r.valide ? 'ok' : r.statut === 'refusee' ? 'attention' : 'neutre'}
                      />
                    }
                  />
                ))}
              </Liste>
            )}
          </EtatRequete>
        </Section>
      </ExigeConnexion>
    </>
  );
}
